import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import {
	AutocompleteGroupedOption,
	UseAutocompleteRenderedOption
} from '@mui/material/useAutocomplete';
import { HTMLAttributes } from 'react';
import { Trans } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { NoResultsFoundIcon } from '@/assets/icons/NoResultsFoundIcon';
import { paths } from '@/core/router/paths';
import { generateQueryString } from '@/utils/url/generateQueryString';

import { QuickSearchOption } from '../../_types/QuickSearch.types';
import { RecentHistory } from '../../RecentHistory/RecentHistory';

import { noQuickSearchResultsStyles as sx } from './NoQuickSearchResults.styles';

type Props = {
	query: string;
	groupedOptions: AutocompleteGroupedOption<QuickSearchOption>[];
	getOptionProps: (
		renderedOption: UseAutocompleteRenderedOption<QuickSearchOption>
	) => HTMLAttributes<HTMLLIElement> & {
		key: string;
	};
	anchorEl: HTMLElement | null;
};

export const NoQuickSearchResults = ({
	query,
	groupedOptions,
	getOptionProps,
	anchorEl
}: Props) => {
	const closePopup = () => {
		anchorEl?.querySelector('input')?.blur();
	};

	return (
		<Box sx={sx.root}>
			<NoResultsFoundIcon sx={sx.searchIcon} />

			<Box sx={sx.description}>
				<Box sx={sx.text} color='text.secondary'>
					<Trans
						defaults='No results for <bold>{{query}}</bold>.'
						values={{ query: query }}
						components={{
							bold: (
								<Typography
									sx={sx.query}
									variant='body1'
									color='text.fadedPrimary'
									fontWeight={700}
									maxWidth={50}
								/>
							)
						}}
					/>
				</Box>

				<Box sx={sx.text} color='text.secondary'>
					<Trans
						defaults='Try a different search term, or use the <bold>Advanced Search</bold>.'
						components={{
							bold: (
								<Link
									component={RouterLink}
									to={{
										pathname: paths.searchHcp,
										search:
											query &&
											generateQueryString({
												name: query
											})
									}}
									fontWeight={700}
									underline='none'
									onClick={closePopup}
								/>
							)
						}}
					/>
				</Box>
			</Box>

			<Box sx={sx.recentHistory}>
				<RecentHistory
					groupedOptions={groupedOptions}
					getOptionProps={getOptionProps}
				/>
			</Box>
		</Box>
	);
};
